import { useEffect, useState } from "react";
import { Rocket, X } from "lucide-react";
import axios from "axios";

const FALLBACK_TEXT =
  "Authors and beta readers have joined Last Bookmark so far. Early Access members are already connecting and working together on real projects while providing ongoing feedback to help shape the Last Bookmark community.";

export default function ActiveBetaBanner() {
  const [count, setCount] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [error, setError] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    let alive = true;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/stats/community`;
    axios
      .get(url, { withCredentials: false })
      .then((r) => {
        if (!alive) return;
        const n = Number(r?.data?.user_count);
        if (Number.isFinite(n)) setCount(n);
        else setError(true);
        if (r?.data?.count_updated_at) setUpdatedAt(r.data.count_updated_at);
      })
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, []);

  const canShowCount = !error && typeof count === "number";
  // Preserve the existing "Updated Aug. 29, 2026" phrasing exactly —
  // abbreviated month + period, numeric day, four-digit year. We always
  // add the period after the month so the layout stays visually
  // identical to what admins have been reading for months.
  let updatedLabel = "Updated Aug. 29, 2026";
  if (updatedAt) {
    const d = new Date(updatedAt);
    if (!Number.isNaN(d.getTime())) {
      const month = d.toLocaleString("en-US", { month: "short" });
      updatedLabel = `Updated ${month}. ${d.getDate()}, ${d.getFullYear()}`;
    }
  }

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        data-testid="active-beta-banner"
        className="relative overflow-hidden border-b border-[hsl(var(--sb-forest))]/25 bg-[hsl(var(--sb-emerald))] text-[hsl(40_60%_96%)]"
      >
        <div className="pointer-events-none absolute inset-0 opacity-15 [background-image:radial-gradient(circle_at_20%_20%,white_0,transparent_45%),radial-gradient(circle_at_80%_60%,hsl(var(--sb-clay))_0,transparent_50%)]" />
        <div className="container-page relative py-3 md:py-3.5 flex flex-col items-center gap-1 text-center">
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-[14px] md:text-[15px] leading-snug font-medium">
          <span
            aria-hidden="true"
            className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[hsl(var(--sb-clay))]/30 shrink-0"
          >
            <Rocket size={13} className="text-white" />
          </span>
          <span className="inline-flex items-center gap-2 shrink-0">
            <span className="font-semibold uppercase tracking-[0.18em] text-[11px] md:text-[11.5px] px-2.5 py-1 rounded-full bg-white/15 border border-white/35 text-white">
              Active Beta
            </span>
            <span aria-hidden="true" className="hidden sm:inline text-white/40 font-normal">
              |
            </span>
          </span>
          {canShowCount ? (
            <span className="min-w-0 max-w-3xl">
              <button
                type="button"
                onClick={() => setShowModal(true)}
                data-testid="active-beta-count-btn"
                className="inline-flex items-baseline font-serif font-bold text-white text-[19px] md:text-[22px] leading-none px-1 hover:text-[hsl(var(--sb-clay))] transition-colors align-baseline"
                aria-label={`${count.toLocaleString()} community members — click to learn more about Last Bookmark's active beta`}
              >
                {count.toLocaleString()}
              </button>
              <span className="text-white/95">
                {` Authors and beta readers have joined Last Bookmark so far. Early Access members are already connecting and working together on real projects while providing ongoing feedback to help shape the Last Bookmark community.`}
              </span>
            </span>
          ) : (
            <span className="min-w-0 max-w-3xl text-white/95">{FALLBACK_TEXT}</span>
          )}
          </div>
          <span
            data-testid="active-beta-updated"
            className="text-[11px] md:text-[12px] font-normal text-white/70 leading-snug"
          >
            {updatedLabel}
          </span>
        </div>
      </div>

      {showModal && <BetaInfoModal onClose={() => setShowModal(false)} />}
    </>
  );
}

function BetaInfoModal({ onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="active-beta-modal-title"
      data-testid="active-beta-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        data-testid="active-beta-modal-backdrop"
        className="absolute inset-0 bg-foreground/40 backdrop-blur-[2px] cursor-default"
      />
      <div className="relative max-w-md w-full rounded-2xl border border-border bg-[hsl(var(--sb-cream))] shadow-xl p-7">
        <button
          type="button"
          onClick={onClose}
          data-testid="active-beta-modal-close"
          className="absolute right-4 top-4 rounded-full p-1.5 text-muted-foreground hover:bg-muted transition-colors"
          aria-label="Close"
        >
          <X size={16} />
        </button>
        <div className="inline-flex items-center gap-2">
          <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-[hsl(var(--sb-clay))]/15">
            <Rocket size={16} className="text-[hsl(var(--sb-clay))]" />
          </span>
          <span className="text-xs uppercase tracking-[0.16em] font-medium text-[hsl(var(--sb-clay))]">
            Active Beta
          </span>
        </div>
        <h2 id="active-beta-modal-title" className="mt-4 font-serif text-2xl leading-tight">
          You&apos;re looking at a living, growing platform.
        </h2>
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
          Last Bookmark is currently in active beta. Every feature is being
          refined with feedback from our growing community of authors and beta
          readers. Thank you for helping us build the future of beta reading!
        </p>
        <button
          type="button"
          onClick={onClose}
          data-testid="active-beta-modal-cta"
          className="btn-primary mt-6"
        >
          Got it — take me back
        </button>
      </div>
    </div>
  );
}
